/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Intent } from 'src/luis/modules/intents/Intent';
import { ScoreFilter } from 'src/luis/projects/utterances/types/Filters';
import { UtteranceWithAnalytics } from 'src/luis/projects/utterances/types/UtteranceWithAnalytics';
import { getNearestRivalIntent } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { FiltersItem } from 'src/verticals/utterancesFilters/types/Filters';

/**
 * Checks the score of the labeled intent for the utterance to
 * be within the limits of the specified filter if exists.
 *
 * @param scoreFilter The score filter to be applied.
 * @param utterance The utterance to be judged.
 */
export const applyScoreFilter = (scoreFilter: ScoreFilter, utterance: UtteranceWithAnalytics): boolean => {
    const labeledIntent = utterance.predictionStore
        ? utterance.predictionStore.predictedIntents.find(
              predictedIntent => predictedIntent.intentId === utterance.utteranceStore.labeledIntentId
          )
        : undefined;

    return (
        (scoreFilter.max === 1 && scoreFilter.min === 0) ||
        (labeledIntent && labeledIntent.score <= scoreFilter.max && labeledIntent.score >= scoreFilter.min)
    );
};

/**
 * Checks if the utterance has one of the labeled entities or
 * predicted ones match with any entity that we filter on.
 *
 * @param appliedEntityFilters The entities filters to be applied.
 * @param utterance The utterance to be judged.
 */
export const applyEntityFilter = (appliedEntityFilters: FiltersItem[], utterance: UtteranceWithAnalytics): boolean =>
    !appliedEntityFilters.length ||
    appliedEntityFilters.some(
        filter =>
            utterance.utteranceStore.labeledEntities.find(labeledEntity => labeledEntity.entityId === filter.id) ||
            utterance.predictionStore?.predictedEntities.find(predictedEntity => predictedEntity.entityId === filter.id)
    );

/**
 * Checks if the nearest rival of the utterance is one of the
 * intents we filtered on.
 *
 * @param appliedIntentFilters The intents filters to be applied.
 * @param utterance The utterance to be judged.
 */
export const applyNearestRivalFilter = (
    appliedIntentFilters: FiltersItem[],
    utterance: UtteranceWithAnalytics,
    intents: Intent[]
): boolean => {
    const nearestRival = getNearestRivalIntent(utterance, intents);

    return !appliedIntentFilters.length || (nearestRival && appliedIntentFilters.some(filter => filter.id === nearestRival.intentId));
};

/**
 * Checks if the utterance is incorrect, unclear or correct
 * if any of them is an applied filter.
 *
 * @param appliedDifferenceFilters The difference filters to be applied.
 * @param utterance The utterance to be judged.
 * @param trainingDate The last training date.
 */
export const applyDifferenceFilter = (
    appliedDifferenceFilters: FiltersItem[],
    utterance: UtteranceWithAnalytics,
    trainingDate: Date
): boolean => {
    const isDifferenceFiltersApplied = appliedDifferenceFilters.length;

    return (
        !isDifferenceFiltersApplied ||
        appliedDifferenceFilters.some(filter => {
            // Checks if the analytics for the current utterance is already calculated.
            if (!utterance.analytics) {
                return false;
            }

            // Checks if the utterance is already modified after last training.
            const isUtteranceModifiedAfterLastTraining = trainingDate < utterance.utteranceStore.modifiedDate;
            if (isUtteranceModifiedAfterLastTraining) {
                return true;
            }

            // Checks if the utterance is incorrect.
            const isIncorrectUtterance = utterance.analytics.incorrectIntents.length;
            // Checks if the utterance is unclear regardless whether it is incorrect .
            const isUnclearUtterance = utterance.analytics.unclearIntents.length;
            // Checks if the utterance is unclear and not incorrect.
            const isUnclearOnlyUtterance = !isIncorrectUtterance && isUnclearUtterance;
            // Checks if the utterance is not incorrect nor unclear which implies that it is correct utterance.
            const isCorrectUtterance = !isIncorrectUtterance && !isUnclearUtterance;

            if (filter.id === 'incorrect') {
                return isIncorrectUtterance;
            }

            if (filter.id === 'unclear') {
                return isUnclearOnlyUtterance;
            }
            if (filter.id === 'correct') {
                return isCorrectUtterance;
            }

            return null;
        })
    );
};
